import React, { useEffect, useState, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import * as Dialog from "@radix-ui/react-dialog";
import { SaveIcon, X } from "lucide-react";
import {
  creationAudioFilterOptionsHooks,
  creationAudiosHooks,
  audioControllerHooks,
  creationAudioGeneratorManager,
} from "@/manager";
import { AudioPlayState } from "@/manager/manager/audio-controller-manager";
import { AudioCondition } from "@/manager/manager/creation-audios-manager";
import {
  PlayIcon,
  PlayLoadingIcon,
  PlayingIcon,
  ChevronLeftThinIcon,
} from "@/components/ui/icon";
import { ProgressSlider } from "@/components/common/progress-slider";
import { TruncatedText } from "@/components/common/truncated-text";
import { Image } from '@/components/common/image';
import { usersUpdateFirstDhCreate } from "@/services/users-update-first-dh-create";
import { processImageUrl } from "@/utils/image-url-resize";
import { showToastByCode } from "@/hooks/use-toast";
import { AudioTable, AudioTableRef } from "./audio-table";
import { TextButton } from "@/components/common/text-button";
import { cn } from "@/lib/utils";
import { Video } from "@/components/common/video";

interface TemplateAudioModalProps {
  open: boolean;
  /** 上传的原文件 */
  file?: File;
  /** 上传文件的类型 */
  uploadType?: "image" | "video";
  cover?: string;
  autoPlay?: boolean;
  defaultGenerateLoading?: boolean;
  defaultAudio?: Audio;
  defaultPlaybackRate?: number;
  showTooltip?: boolean;
  isBlinking?: boolean;
  onOpenChange: (open: boolean) => void;
  onBeforePlayPause?: (audio: Audio) => void;
  onBeforeSelect?: (audio: Audio) => void;
  onPlayDefaultAudio?: (audio: Audio) => Promise<void>;
  onChangeDefaultFavorite?: (favorite: boolean) => void;
  onChange: ({
    audio,
    playbackRate,
  }: {
    audio: Audio;
    playbackRate: number;
  }) => void;
}

export const TemplateAudioModal = ({
  open,
  file,
  uploadType,
  cover,
  autoPlay,
  defaultGenerateLoading,
  defaultAudio,
  defaultPlaybackRate,
  showTooltip,
  isBlinking,
  onOpenChange,
  onBeforePlayPause,
  onBeforeSelect,
  onPlayDefaultAudio,
  onChangeDefaultFavorite,
  onChange,
}: TemplateAudioModalProps) => {
  const { t } = useTranslation();
  const { options } = creationAudioFilterOptionsHooks.useOptions();
  const {
    audios,
    loading,
    name,
    condition,
    filters,
    changeName,
    changeCondition,
    changeFilters,
  } = creationAudiosHooks.useAudios();
  const { toggleFavorite } = creationAudiosHooks.useToggleFavorite();
  const { addMyVoice } = creationAudiosHooks.useAddMyVoice();
  const { rename } = creationAudiosHooks.useRename();
  const {
    playState,
    duration,
    currentTime,
    playbackRate,
    play,
    pause,
    stop,
    seekTo,
    startPlaybackRateDrag,
    endPlaybackRateDrag,
    setPlaybackRateWhileDragging,
  } = audioControllerHooks.useController();
  const [playingCurrentTime, setPlayingCurrentTime] = useState(0);
  const [selectDuration, setSelectDuration] = useState(0);
  const [selectCurrentTime, setSelectCurrentTime] = useState(0);
  const [selectPlaybackRate, setSelectPlaybackRate] = useState(1);
  const [selectAudio, setSelectAudio] = useState<Audio | null>(defaultAudio);
  const [playingAudio, setPlayingAudio] = useState<Audio | null>(null);
  const audioTableRef = useRef<AudioTableRef>(null);

  // 处理弹窗打开关闭
  const handleOpenChange = (open: boolean) => {
    onOpenChange(open);
    stop();
  };

  // 添加播放/暂停处理函数
  const handlePlayPause = async (audio: Audio) => {
    // 播放前调用回调
    onBeforePlayPause?.(audio);
    // 播放的新音频不是当前播放的音频时重置当前播放音频进度
    if (audio.id !== playingAudio?.id) {
      // 提前重置状态 避免进度条展示混乱
      setPlayingCurrentTime(0);
      setSelectCurrentTime(0);
      setSelectDuration(0);
      setPlayingAudio(audio);
      // 停止当前播放的音频
      stop();
      // 判断当前音频是否是默认音频 如果是且没有处理过（autoPlay为false）则需要通过onPlayDefaultAudio回调去处理
      if (audio.id === defaultAudio?.id && !autoPlay) {
        await onPlayDefaultAudio?.(audio);
        return;
      }
      // 播放demo
      play(audio.audioUrl);
      return;
    }
    playState === AudioPlayState.PLAYING ? pause() : play(audio.audioUrl);
  };

  // 处理添加我的语音
  const handleAddMyVoice = async (audio: Audio) => {
    addMyVoice(audio);
    // 默认选中添加的语音
    setSelectAudio(audio);
  };

  // 处理选择音频
  const handleSelect = async (audio: Audio) => {
    // 已选中不处理
    if (audio.id === selectAudio?.id) {
      return;
    }
    // 正在播放仅选中即可
    if (audio.id === playingAudio?.id) {
      setSelectAudio(audio);
      setSelectCurrentTime(currentTime);
      setSelectDuration(duration);
      return;
    }
    // 选中前调用回调
    onBeforeSelect?.(audio);
    // 提前重置状态 避免进度条展示混乱
    setPlayingCurrentTime(0);
    setSelectCurrentTime(0);
    setSelectDuration(0);
    // 停止当前播放的音频
    stop();
    // 判断当前音频是否是默认音频 如果是且没有处理过（autoPlay为false）则需要通过onPlayDefaultAudio回调去处理
    if (audio.id === defaultAudio?.id && !autoPlay) {
      // 先触发状态更新页面显示
      setSelectAudio(audio);
      setPlayingAudio(audio);
      await onPlayDefaultAudio?.(audio);
      return;
    }
    // 设置选中音频和播放音频
    setSelectAudio(audio);
    setPlayingAudio(audio);
    // 播放demo
    play(audio.audioUrl);
  };

  // 切换语音倍数
  const handleChangeSpeed = (speed: number) => {
    setPlaybackRateWhileDragging(speed);
  };

  // 切换收藏
  const handleToggleFavorite = async (audio: Audio) => {
    const isDefaultAudio = audio.id === defaultAudio?.id;
    // 如果切换收藏处理的是默认音频则需要单独处理，因为默认音频是后添加进入列表的。
    if (isDefaultAudio) {
      // 切换默认音频收藏状态乐观更新
      const isStarred = !defaultAudio?.isStarred;
      onChangeDefaultFavorite?.(isStarred);
      try {
        await toggleFavorite(audio.id);
      } catch {
        // 切换收藏失败则回滚状态
        onChangeDefaultFavorite?.(!isStarred);
      }
      return;
    }
    toggleFavorite(audio.id);
  };

  // 搜索名称改变
  const handleSearchNameChange = (name: string) => {
    changeName(name);
  };

  // 条件改变
  const handleConditionChange = (condition: AudioCondition) => {
    changeCondition(condition);
  };

  // 筛选条件改变
  const handleFilterChange = (key: string, value: string) => {
    changeFilters({ ...filters, [key]: value });
  };

  // 保存选中音频
  const save = () => {
    onChange({
      audio: selectAudio,
      playbackRate: selectPlaybackRate,
    });
    handleOpenChange(false);
  };

  // 滚动到当前选中的音频
  const handleScrollToCurrentAudio = () => {
    audioTableRef.current?.scrollToCurrentAudio();
  };

  // 格式化时间显示
  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return time ? `${minutes}:${seconds.toString().padStart(2, "0")}` : "0:00";
  };

  // 格式化播放速度
  const formatSpeed = (speed: number) => {
    return speed.toFixed(1);
  };

  // 渲染当前选中音频的播放按钮
  const renderCurrentAudioController = () => {
    // 闪烁样式：当tooltip激活且isBlinking为true时应用紫色
    const blinkStyle =
      showTooltip && isBlinking ? { color: "rgb(117, 66, 247)" } : {};

    if (playingAudio?.id !== selectAudio?.id) {
      return (
        <PlayIcon
          className={`w-full h-full text-i-normal hover:text-[rgba(40,47,62,1)] ${showTooltip ? "transition-all duration-1000" : ""
            }`}
          style={blinkStyle}
        />
      );
    }
    if (defaultGenerateLoading || playState === AudioPlayState.LOADING) {
      return <PlayLoadingIcon className="w-full h-full" />;
    }
    if (playState === AudioPlayState.PLAYING) {
      return (
        <PlayingIcon
          className="w-full h-full text-i-normal hover:text-[rgba(40,47,62,1)]"
          progress={Math.ceil((selectCurrentTime / selectDuration) * 100)}
        />
      );
    }

    return (
      <PlayIcon
        className={cn(
          `w-full h-full text-i-normal hover:text-[rgba(40,47,62,1)]`,
          showTooltip ? "transition-all duration-1000" : "",
          !selectAudio?.id && "text-[#70747E] hover:text-[#70747E] cursor-auto"
        )}
        style={blinkStyle}
      />
    );
  };

  // 同步选中音频
  useEffect(() => {
    // 定制数字人-音频，选择 Audit Template 时启用的默认数据
    const defaultData = {
      name: "Unnamed",
    }
    setSelectAudio(defaultAudio?.id ? defaultAudio : defaultData as unknown as Audio);
    // 默认播放
    if (autoPlay) {
      setPlayingAudio(defaultAudio);
      play(defaultAudio.audioUrl);
    }
  }, [defaultAudio, autoPlay]);

  // 同步选中音频的播放速度
  useEffect(() => {
    setSelectPlaybackRate(defaultPlaybackRate);
  }, [defaultPlaybackRate]);

  // 同步选中音频的时长
  useEffect(() => {
    if (playingAudio?.id === selectAudio?.id) {
      setSelectDuration(duration);
    }
  }, [duration]);

  // 同步选中音频播放进度
  useEffect(() => {
    setPlayingCurrentTime(currentTime);
    if (playingAudio?.id === selectAudio?.id) {
      setSelectCurrentTime(currentTime);
    }
  }, [currentTime]);

  // 同步选中音频播放速度
  useEffect(() => {
    setSelectPlaybackRate(playbackRate);
  }, [playbackRate]);

  // 实际需要展示的音频列表
  let showAudios = audios;
  if (defaultAudio) {
    // 尝试查找音频列表中是否存在默认音频
    const targetAudio = audios.find((audio) => {
      return audio.id === defaultAudio.id;
    });
    // 只有默认音频存在在列表中才处理
    if (targetAudio) {
      // 过滤掉在列表中的默认音频
      const filterAudios = audios.filter((audio) => {
        return audio.id !== targetAudio.id;
      });
      // 合并音频数据
      const merageDefaultAudio = {
        ...targetAudio,
        audioUrl: defaultAudio.audioUrl,
      };
      // 把默认音频放在最上面
      showAudios = [merageDefaultAudio, ...filterAudios];
    }
  }
  showAudios =
    // 特殊条件过滤处理 收藏状态可以由前端调控，所以也需要在前端进行筛选处理。
    condition === "starred_voices"
      ? showAudios.filter((audio) => audio.isStarred)
      : showAudios;

  const originFileUrl = useMemo(() => {
    if (!file) return;
    return processImageUrl(URL.createObjectURL(file), 48)
  }, [file])

  return (
    <Dialog.Root open={open} onOpenChange={(open) => handleOpenChange(open)}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 z-50" />
        <Dialog.Content
          className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-[1.25rem] shadow-lg w-[90vw] h-[90vh] max-w-[1080px] max-h-[768px] z-50 flex flex-col focus:outline-none"
        >
          <div className="flex justify-between items-center p-6 border-b">
            <Dialog.Title className="text-i-normal text-lg font-semibold">
              {t("modal_audio.index.audio_settings")}
            </Dialog.Title>
            <Dialog.Close asChild>
              <button
                className="w-10 h-10 flex justify-center items-center hover:bg-gray-100 border border-[rgba(208,213,221,1)] rounded-sm shadow-[0px_1px_2px_rgba(16,24,40,0.05)]"
                tabIndex={-1}
              >
                <X className="w-4 h-4" />
              </button>
            </Dialog.Close>
          </div>
          <div className="flex flex-1 overflow-hidden">
            {/* 左侧音频设置区域 */}
            <div className="w-80 p-6 flex flex-col border-r">
              {/* 当前选中的音频信息 */}
              <div className="mb-4 border-b">
                <div className="flex items-center gap-3 mb-4">
                  <div className="overflow-hidden w-9 h-12 bg-purple-100 rounded-sm flex items-center justify-center">
                    {uploadType === "image" ? (
                      <Image src={cover || originFileUrl} />
                    ) : (
                      <Video
                        src={cover || originFileUrl}
                        autoPlay={false}
                      />
                    )}
                  </div>
                  <div className="flex-1">
                    <TruncatedText
                      className="w-32 font-semibold text-base text-i-normal capitalize"
                      tooltipClassName="capitalize"
                      as="h3"
                      text={selectAudio?.name}
                    />
                    <div className="flex items-center gap-2">
                      <ProgressSlider
                        value={selectCurrentTime}
                        max={selectDuration}
                        trackColor="rgb(234,236,240)"
                        progressColor={selectAudio?.id ? "rgb(16, 24, 40)" : "rgb(234,236,240)"}
                        thumbColor="rgb(255,255,255)"
                        thumbBorderColor="rgb(16, 24, 40)"
                        onChange={seekTo}
                        onDragStart={() => pause()}
                        onDragEnd={() => play(selectAudio.audioUrl)}
                        disabled={playState === AudioPlayState.LOADING || !selectAudio?.id}
                        className="flex-1 "
                      />
                      <div className="w-8 text-sm text-i-normal">
                        {formatTime(
                          (selectDuration - selectCurrentTime) /
                          selectPlaybackRate
                        )}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      if (!selectAudio?.id) return
                      handlePlayPause(selectAudio)
                    }}
                    className="w-10 h-10"
                    tabIndex={-1}
                  >
                    {renderCurrentAudioController()}
                    {showTooltip && (
                      <div className="z-10 absolute top-28 left-[17rem] px-2 py-1 w-fit transform -translate-y-[100%] text-sm text-white font-medium bg-[rgba(117,66,247,1)] rounded-sm rounded-bl-none">
                        {t("modal_audio.index.tooltip")}
                      </div>
                    )}
                  </button>
                </div>

                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs text-i-sub">
                    {t("modal_audio.index.voice_over")}
                  </span>
                  {selectAudio?.id && <button
                    onClick={handleScrollToCurrentAudio}
                    className="max-w-[50%] flex items-center bg-[rgb(249,250,251)] px-2 py-1 gap-0.5 rounded-sm text-sm text-i-normal font-medium hover:bg-gray-200 transition-colors"
                    tabIndex={-1}
                  >
                    <TruncatedText
                      className="min-w-16 capitalize"
                      tooltipClassName="capitalize"
                      text={selectAudio?.name}
                    />
                    <ChevronLeftThinIcon className="w-4 h-4" />
                  </button>}
                </div>

                <TruncatedText
                  className="w-full text-xs text-i-black mb-4 capitalize"
                  tooltipClassName="capitalize"
                  text={selectAudio?.id ? `${t(selectAudio?.gender)},${t(
                    selectAudio?.ageGroup
                  )},${t(selectAudio?.language)}` : t("common.none")}
                />
              </div>
              {/* 语速控制 */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-i-sub">
                    {t("modal_audio.index.speed")}
                  </span>
                  <span className="text-xs font-medium text-i-normal">
                    {formatSpeed(playbackRate)}x
                  </span>
                </div>
                <div className="relative">
                  <ProgressSlider
                    value={playbackRate}
                    height={12}
                    trackColor="rgb(255,255,255)"
                    trackBorderColor="rgb(234,236,240)"
                    progressColor="rgb(16,24,40)"
                    thumbColor="rgb(255,255,255)"
                    thumbBorderColor="rgb(0,0,0)"
                    thumb={
                      <div className="w-7 h-7 bg-white border-2 border-i-normal rounded-full shadow-lg flex items-center justify-center hover:scale-105 transition-all duration-200">
                        {/* 六个小圆点 */}
                        <div className="grid grid-cols-2 gap-0.5">
                          {Array.from({ length: 6 }).map((_, index) => (
                            <div
                              key={index}
                              className="w-[3px] h-[3px] bg-[rgb(152,162,179)] rounded-full"
                            />
                          ))}
                        </div>
                      </div>
                    }
                    alwaysShowThumb
                    max={2.0}
                    onChange={handleChangeSpeed}
                    onDragStart={startPlaybackRateDrag}
                    onDragEnd={endPlaybackRateDrag}
                  />
                </div>
              </div>
              {/* 保存按钮 */}
              <TextButton
                onClick={save}
                className="mt-auto w-full h-10 text-sm font-semibold"
                tabIndex={-1}
                label={t("modal_audio.index.save_this_voice")}
                icon={<SaveIcon className="w-5 h-5" />}
                disabled={!selectAudio?.id}
              />
            </div>

            {/* 右侧音频表格区域 */}
            <AudioTable
              ref={audioTableRef}
              audios={showAudios}
              filters={filters}
              selectAudio={selectAudio}
              playingAudio={playingAudio}
              currentTime={playingCurrentTime}
              duration={duration}
              playState={
                defaultAudio?.id === playingAudio?.id && defaultGenerateLoading
                  ? AudioPlayState.LOADING
                  : playState
              }
              searchName={name}
              condition={condition}
              filterOptions={options}
              loading={loading}
              onSearchNameChange={handleSearchNameChange}
              onFilterChange={handleFilterChange}
              onConditionChange={handleConditionChange}
              onVoiceRename={rename}
              onVoiceAdd={handleAddMyVoice}
              onVoiceSelect={handleSelect}
              onPlayPause={handlePlayPause}
              onToggleFavorite={handleToggleFavorite}
            />
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

interface AudioModalProps {
  open: boolean;
  tooltip: boolean;
  text: string;
  actor: Actor;
  audioState: AudioState;
  onOpenChange: (open: boolean) => void;
  onAudioStateChange: (e: { audio: Audio; playbackRate: number }) => void;
}

export const AudioModal: React.FC<AudioModalProps> = ({
  open,
  tooltip,
  text,
  actor,
  audioState: defaultAudioState,
  onOpenChange,
  onAudioStateChange,
}) => {
  const [defaultGenerateLoading, setDefaultGenerateLoading] = useState(false);
  const [defaultAudio, setDefaultAudio] = useState(defaultAudioState.audio);
  const [autoPlay, setAutoPlay] = useState(false);
  const [showTooltip, setShowTooltip] = useState(tooltip);
  const [isBlinking, setIsBlinking] = useState(false);
  const blinkIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // 音频播放前处理
  const onBeforePlayPause = () => {
    usersUpdateFirstDhCreate();
    setShowTooltip(false);
  };

  // 音频选择前处理
  const onBeforeSelect = () => {
    usersUpdateFirstDhCreate();
    setShowTooltip(false);
  };

  // 处理默认音频播放
  const onPlayDefaultAudio = async () => {
    // 已处于自动播放状态 说明已经处理过默认音频播放逻辑 不需要再重新处理一般
    if (autoPlay) {
      return;
    }
    setDefaultGenerateLoading(true);
    try {
      const data = await creationAudioGeneratorManager.generate({
        text,
        actorId: actor.id,
        voiceTtsId: defaultAudioState.audio.voiceTtsId,
        modelName: defaultAudioState.audio.modelName,
        voiceSettings: {
          stability: 0,
          similarityBoost: 0,
          styleExaggeration: 0,
          speed: 1,
        },
        audioName: "",
      });
      // 设置默认音频
      setDefaultAudio({
        ...defaultAudio,
        audioUrl: data.audioUrl,
      });
      // 设置自动播放
      setAutoPlay(true);
    } catch (error) {
      showToastByCode(error);
    }
    setDefaultGenerateLoading(false);
  };

  // 处理默认音频收藏状态改变
  const onChangeDefaultFavorite = (favorite: boolean) => {
    setDefaultAudio((defaultAudio) => ({
      ...defaultAudio,
      isStarred: favorite,
    }));
  };

  // 处理音频选择
  const onChange = (data: { audio: Audio; playbackRate: number }) => {
    onAudioStateChange(data);
  };

  // 处理tooltip显示
  useEffect(() => {
    setShowTooltip(tooltip);

    if (tooltip) {
      // 开始闪烁动画
      setIsBlinking(true);
      blinkIntervalRef.current = setInterval(() => {
        setIsBlinking((prev) => !prev);
      }, 1500);

      // 5秒后停止tooltip和闪烁
      setTimeout(() => {
        setShowTooltip(false);
        setIsBlinking(false);
        if (blinkIntervalRef.current) {
          clearInterval(blinkIntervalRef.current);
          blinkIntervalRef.current = null;
        }
      }, 5000);
    } else {
      // 清理闪烁动画
      setIsBlinking(false);
      if (blinkIntervalRef.current) {
        clearInterval(blinkIntervalRef.current);
        blinkIntervalRef.current = null;
      }
    }

    // 清理函数
    return () => {
      if (blinkIntervalRef.current) {
        clearInterval(blinkIntervalRef.current);
        blinkIntervalRef.current = null;
      }
    };
  }, [tooltip]);

  return (
    <TemplateAudioModal
      open={open}
      cover={processImageUrl(actor.image, 48)}
      defaultGenerateLoading={defaultGenerateLoading}
      defaultAudio={defaultAudio}
      defaultPlaybackRate={defaultAudioState.playbackRate}
      autoPlay={autoPlay}
      showTooltip={showTooltip}
      isBlinking={isBlinking}
      onOpenChange={onOpenChange}
      onBeforePlayPause={onBeforePlayPause}
      onBeforeSelect={onBeforeSelect}
      onPlayDefaultAudio={onPlayDefaultAudio}
      onChangeDefaultFavorite={onChangeDefaultFavorite}
      onChange={onChange}
    />
  );
};
